import { AnalyticsUserEvent } from '@fruitful/types';
import { type Href, router } from 'expo-router';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import {
    ArrowRightIcon,
    BadgeLabel,
    BodyMediumEmphasized,
    BodySmallRegular,
    type ColorTokenKey,
    H2Emphasized,
    Icon,
    type IconType,
    Image,
    isNonMobileWeb,
    ScreenContainer,
    TileContainer,
    XStack,
    YStack,
} from 'smoothie';

import BeeFlowers from '../../../../assets/images/illustrations/bee-flowers.png';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { track } from '../../../logger/Logger';
import { selectedAccountForDirectDepositAtom } from '../../state/SpacesAtoms';
import type { DirectDepositSwitchAppContext } from './types';

interface Props {
    context?: DirectDepositSwitchAppContext | '';
    source?: string;
}

const Explainer = ({ context = '', source = '' }: Props) => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce(`${context}/${source}/DirectDeposit/Explainer`);

    const isFromMoneyMap = context === 'roadmap';
    const redirectHref: Href = isFromMoneyMap
        ? '/home/roadmap'
        : '/home/banking';

    const selectedAccount = useAtomValue(selectedAccountForDirectDepositAtom);

    // For a money map we want to let the member select, but default to their
    // income account.
    const needsToSelectAccount = selectedAccount === null || isFromMoneyMap;

    const continueAction = () => {
        router.replace({
            pathname: '/banking/direct-deposit/switch',
            params: {
                context,
                source,
            },
        });
    };

    const bankAccountId =
        isFromMoneyMap && selectedAccount !== null
            ? selectedAccount.bankAccountId
            : undefined;

    const selectAccountAction = () => {
        router.push({
            pathname: '/banking/direct-deposit/select-account',
            params: {
                bankAccountId,
                context,
                source,
            },
        });
    };

    const switchManuallyAction = () => {
        track(AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_VIEW_MANUAL, {
            ddAppContext: context,
            ddSource: source,
        });
        router.push({
            pathname: '/banking/direct-deposit/manual-switch',
            params: {
                bankAccountId,
                context,
                source,
            },
        });
    };

    return (
        <ScreenContainer
            hasCloseButton
            onClose={() => {
                track(AnalyticsUserEvent.DIRECT_DEPOSIT_SWITCH_EXPLAINER_EXIT, {
                    ddAppContext: context,
                    ddSource: source,
                });
                router.navigate(redirectHref);
            }}
            containerProps={{
                justifyContent: isNonMobileWeb ? 'center' : 'space-between',
            }}
        >
            <YStack alignItems="center" $sm={{ maxHeight: 350 }}>
                <YStack
                    maxWidth="75%"
                    alignItems="center"
                    $sm={{ maxWidth: '85%' }}
                >
                    <Image
                        source={BeeFlowers}
                        objectFit="contain"
                        style={{
                            width: isNonMobileWeb ? 186 : 146,
                            height: isNonMobileWeb ? 186 : 146,
                            margin: 32,
                        }}
                    />
                    <H2Emphasized textAlign="center">
                        Switch your direct deposit to Fruitful
                    </H2Emphasized>
                </YStack>
            </YStack>
            <YStack
                width="100%"
                maxWidth={600}
                alignSelf="center"
                gap={12}
                marginTop={isNonMobileWeb ? 60 : 32}
                marginBottom={isNonMobileWeb ? 20 : 0}
            >
                <DirectDepositNavigationTile
                    title="Switch with Pinwheel"
                    showFastestBadge={true}
                    description="Pinwheel lets you securely switch paycheck by connecting your payroll in a few steps."
                    iconRight={ArrowRightIcon}
                    onPress={
                        needsToSelectAccount
                            ? selectAccountAction
                            : continueAction
                    }
                />

                <DirectDepositNavigationTile
                    title="Switch manually"
                    description="Enter your Fruitful account info in your payroll provider to switch your paycheck."
                    iconRight={ArrowRightIcon}
                    onPress={switchManuallyAction}
                />
            </YStack>
        </ScreenContainer>
    );
};

const DirectDepositNavigationTile = ({
    title,
    description,
    iconRight,
    iconRightColor = '$gray1200',
    iconRightSize = 24,
    onPress,
    backgroundColor = '$gray200',
    showFastestBadge = false,
}: DirectDepositNavigationTileProps) => {
    return (
        <TileContainer backgroundColor={backgroundColor} onPress={onPress}>
            <YStack flex={6}>
                <XStack gap={8} alignItems="center">
                    <BodyMediumEmphasized color="$black" paddingVertical={2}>
                        {title}
                    </BodyMediumEmphasized>
                    {showFastestBadge && (
                        <BadgeLabel
                            variant="positive"
                            text="Fastest"
                            size="regular"
                        />
                    )}
                </XStack>

                <BodySmallRegular color="$gray1000">
                    {description}
                </BodySmallRegular>
            </YStack>
            {iconRight && (
                <XStack
                    alignItems="center"
                    justifyContent="center"
                    width={32}
                    height={32}
                >
                    <Icon
                        icon={iconRight}
                        size={iconRightSize}
                        color={iconRightColor}
                    />
                </XStack>
            )}
        </TileContainer>
    );
};

interface DirectDepositNavigationTileProps {
    title: string;
    description?: string;
    iconRight?: IconType;
    iconRightColor?: ColorTokenKey;
    iconRightSize?: number;
    onPress?: () => void;
    backgroundColor?: ColorTokenKey;
    showFastestBadge?: boolean;
}

export default Explainer;
